import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useAuth0 } from "@auth0/auth0-react";
import { Message, useToaster, Whisper, Popover } from "rsuite";
import { DateTime } from "luxon";
import PlusIcon from "@rsuite/icons/Plus";
import DetailIcon from "@rsuite/icons/Detail";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import { Button, GlassCard, Badge } from "../../../../ui";
import { UpdateIssue } from "../../admin/IssueTracker/Modal/UpdateIssue";
import { CreateIssue } from "./Modal/CreateIssue";

export const SystemIssues = ({ systemId, metadata }) => {
  const { getAccessTokenSilently } = useAuth0();
  const toaster = useToaster();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [update, setUpdate] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const getSystemIssues = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const res = await axiosInstance({
        url: `/network/issues`,
        headers: { authorization: `Bearer ${token}` },
      });

      // Filter issues for this specific system
      const systemIssues = res?.data[0]?.filter(
        (issue) => issue.id === systemId && issue.status === "active",
      );
      setIssues(systemIssues || []);
    } catch (error) {
      console.error("Error fetching issues:", error);
      toaster.push(
        <Message showIcon type="error" header="Error">
          Failed to load system issues
        </Message>,
        "topCenter",
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    if (systemId) {
      getSystemIssues();
    }
  }, [systemId, update]);

  const getOpenIssuesCount = () => {
    return issues.filter((issue) => !issue.resolved).length;
  };

  const getClosedIssuesCount = () => {
    return issues.filter((issue) => issue.resolved).length;
  };

  const getTypeVariant = (type) => {
    switch (type) {
      case "OFFLINE":
        return "error";
      case "MAGNET":
        return "warning";
      case "EDU":
        return "info";
      case "Credentials":
        return "warning";
      case "Host Configuration":
        return "info";
      default:
        return "info";
    }
  };

  if (loading) {
    return (
      <IssuesContainer>
        <GlassCard>
          <LoadingText>Loading issues...</LoadingText>
        </GlassCard>
      </IssuesContainer>
    );
  }

  return (
    <IssuesContainer>
      {/* Header Section */}
      <HeaderCard>
        <IssuesHeader>
          <HeaderLeft>
            <IssuesTitle>System Issues</IssuesTitle>
            <IssueStats>
              <StatBadge variant="error">{getOpenIssuesCount()} Open</StatBadge>
              <StatBadge variant="success">
                {getClosedIssuesCount()} Closed
              </StatBadge>
            </IssueStats>
          </HeaderLeft>
          <Button
            variant="primary"
            size="small"
            onClick={() => setShowCreateModal(true)}
          >
            <PlusIcon style={{ marginRight: "0.5rem" }} />
            Create Issue
          </Button>
        </IssuesHeader>
      </HeaderCard>

      {issues.length === 0 ? (
        <GlassCard>
          <EmptyState>
            <EmptyIcon>✓</EmptyIcon>
            <EmptyTitle>No Issues</EmptyTitle>
            <EmptyDescription>
              This system has no active issues. Click "Create Issue" to report a
              problem.
            </EmptyDescription>
          </EmptyState>
        </GlassCard>
      ) : (
        <>
          {/* Table Header */}
          <TableHeader>
            <HeaderCell flex="0.4"></HeaderCell>
            <HeaderCell flex="1.5">Reported</HeaderCell>
            <HeaderCell flex="0.8">Days</HeaderCell>
            <HeaderCell flex="1.2">Type</HeaderCell>
            <HeaderCell flex="1.5">Assigned</HeaderCell>
            <HeaderCell flex="1">Reportable</HeaderCell>
            <HeaderCell flex="0.8">Actions</HeaderCell>
          </TableHeader>

          {/* Issues List */}
          <IssuesList>
            {issues.map((issue) => (
              <IssueCard key={issue.report_id} resolved={issue.resolved}>
                <IssueRow>
                  <IssueCell flex="0.4">
                    <Whisper
                      placement="top"
                      speaker={
                        <Popover
                          title="Issue Details"
                          style={{ width: "400px" }}
                        >
                          <PopoverContent>
                            <DetailSection>
                              <DetailLabel>Notes:</DetailLabel>
                              <DetailText>{issue.notes}</DetailText>
                            </DetailSection>
                            <DetailSection>
                              <DetailLabel>Diagnosis:</DetailLabel>
                              <DetailText>
                                {issue.diagnoses || "No diagnosis yet"}
                              </DetailText>
                            </DetailSection>
                            <DetailSection>
                              <DetailLabel>Updated By:</DetailLabel>
                              <DetailText>{issue.updated_by}</DetailText>
                            </DetailSection>
                            <DetailSection>
                              <DetailLabel>Last Updated:</DetailLabel>
                              <DetailText>
                                {DateTime.fromISO(issue.updated_at).toFormat(
                                  "ff",
                                )}
                              </DetailText>
                            </DetailSection>
                            {issue.reportable && (
                              <DetailSection>
                                <DetailLabel>Report Users:</DetailLabel>
                                <UsersList>
                                  {issue.report_users?.map((user, index) => (
                                    <UserItem key={index}>{user}</UserItem>
                                  ))}
                                </UsersList>
                              </DetailSection>
                            )}
                          </PopoverContent>
                        </Popover>
                      }
                      trigger="hover"
                    >
                      <DetailIcon
                        style={{
                          fontSize: "1.5em",
                          color: "#4a90e2",
                          cursor: "pointer",
                        }}
                      />
                    </Whisper>
                  </IssueCell>

                  <IssueCell flex="1.5">
                    <DateTimeText>
                      {DateTime.fromISO(issue.inserted_at).toFormat(
                        "MMM dd, yyyy",
                      )}
                    </DateTimeText>
                    <TimeStamp>
                      {DateTime.fromISO(issue.inserted_at).toFormat("h:mm a")}
                    </TimeStamp>
                  </IssueCell>

                  <IssueCell flex="0.8">
                    <DaysBadge variant={issue.resolved ? "success" : "error"}>
                      {issue.resolved
                        ? Math.floor(
                            DateTime.local().diff(
                              DateTime.fromISO(issue.updated_at),
                              "days",
                            ).days,
                          )
                        : Math.floor(
                            DateTime.local().diff(
                              DateTime.fromISO(issue.inserted_at),
                              "days",
                            ).days,
                          )}
                      d
                    </DaysBadge>
                  </IssueCell>

                  <IssueCell flex="1.2">
                    <TypeBadge variant={getTypeVariant(issue.type)}>
                      {issue.type}
                    </TypeBadge>
                  </IssueCell>

                  <IssueCell flex="1.5">
                    <AssignedTo>{issue.assigned || "Unassigned"}</AssignedTo>
                  </IssueCell>

                  <IssueCell flex="1">
                    {issue.reportable ? (
                      <Whisper
                        placement="left"
                        speaker={
                          <Popover
                            title="Report Users"
                            style={{ width: "300px" }}
                          >
                            <PopoverContent>
                              <UsersList>
                                {issue.report_users?.map((user, index) => (
                                  <UserItem key={index}>{user}</UserItem>
                                ))}
                              </UsersList>
                            </PopoverContent>
                          </Popover>
                        }
                        trigger="hover"
                      >
                        <ReportableStatus variant="success">
                          YES
                        </ReportableStatus>
                      </Whisper>
                    ) : (
                      <ReportableStatus variant="error">NO</ReportableStatus>
                    )}
                  </IssueCell>

                  <IssueCell flex="0.8">
                    <ActionButtons>
                      <UpdateIssue
                        issue={issue}
                        update={update}
                        setUpdate={setUpdate}
                      />
                    </ActionButtons>
                  </IssueCell>
                </IssueRow>
              </IssueCard>
            ))}
          </IssuesList>
        </>
      )}

      <CreateIssue
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        systemId={systemId}
        metadata={metadata}
        onSuccess={() => {
          setUpdate(!update);
          setShowCreateModal(false);
        }}
      />
    </IssuesContainer>
  );
};

// Styled Components
const IssuesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 1rem;

  @media (max-width: 1400px) {
    max-width: 90%;
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const HeaderCard = styled(GlassCard)`
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const IssuesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const IssuesTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: rgba(0, 0, 0, 0.8);
`;

const IssueStats = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const StatBadge = styled(Badge)`
  font-size: 0.75rem;
  padding: 0.15rem 0.5rem;
`;

const LoadingText = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.9rem;
`;

const EmptyState = styled.div`
  padding: 2rem 1.5rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
`;

const EmptyIcon = styled.div`
  font-size: 2rem;
  opacity: 0.3;
`;

const EmptyTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: rgba(0, 0, 0, 0.6);
`;

const EmptyDescription = styled.p`
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.5);
  max-width: 400px;
  margin: 0;
  line-height: 1.4;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 1.25rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  backdrop-filter: blur(10px);
`;

const HeaderCell = styled.div`
  flex: ${({ flex }) => flex || 1};
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const IssuesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const IssueCard = styled(GlassCard)`
  border-left: 4px solid ${({ resolved }) => (resolved ? "#27ae60" : "#e74c3c")};
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }
`;

const IssueRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 1.5rem;
  min-height: 50px;
`;

const IssueCell = styled.div`
  flex: ${({ flex }) => flex || 1};
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 35px;
`;

const DateTimeText = styled.div`
  font-size: 0.85rem;
  color: #2c3e50;
  font-weight: 500;
`;

const TimeStamp = styled.div`
  font-size: 0.75rem;
  color: #7f8c8d;
`;

const DaysBadge = styled(Badge)`
  font-weight: 600;
  font-size: 0.75rem;
  margin-right: 1rem;
`;

const TypeBadge = styled(Badge)`
  margin-right: 1rem;
  font-size: 0.75rem;
`;

const AssignedTo = styled.div`
  font-size: 0.8rem;
  color: #5d6d7e;
  font-style: ${({ children }) =>
    children === "Unassigned" ? "italic" : "normal"};
  opacity: ${({ children }) => (children === "Unassigned" ? 0.6 : 1)};
`;

const ReportableStatus = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ variant }) => (variant === "success" ? "#27ae60" : "#e74c3c")};
  cursor: ${({ variant }) => (variant === "success" ? "pointer" : "default")};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.35rem;
  align-items: center;
`;

// Popover styles
const PopoverContent = styled.div`
  padding: 0.5rem;
`;

const DetailSection = styled.div`
  margin-bottom: 1rem;
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.div`
  font-weight: 600;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
  color: #333;
`;

const DetailText = styled.div`
  font-size: 0.8rem;
  color: #666;
  line-height: 1.4;
`;

const UsersList = styled.ul`
  margin: 0;
  padding-left: 1rem;
`;

const UserItem = styled.li`
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0.25rem;
`;
